import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { BootstrapRequest, CompleteAccountRequest } from '../types/community'
import { ageFromDob, todayEnGb } from '../lib/age'
import {
  DATA_NOTICE_VERSION,
  noticeCards,
  noticeConsentTick,
  noticeLede,
  signupChildConsentText,
} from '../lib/legal/cotNotices'
import { getAcknowledgements, type AckRequirement } from '../lib/api/signup'

export type SignupMode = 'family' | 'solo'
export type Relationship = 'parent' | 'guardian' | 'foster' | 'kinship' | 'self'
export type Supervision = 'strict' | 'view' | 'self'
export type VerificationMethod = 'card' | 'id' | 'form' | 'call'

export interface SignupKid {
  first: string
  dob: string
  supervision: Supervision
  consent: boolean
}

export interface SignupDraft {
  step: number
  mode: SignupMode
  name: string
  email: string
  dob: string
  rel: Relationship
  notice: boolean
  /** Version actually displayed when the account holder ticked the notice. Never inferred on load. */
  noticeVersion: string | null
  acks: Record<string, boolean>
  ackVersions: Record<string, string>
  method: VerificationMethod | null
  familyId?: string
  savedAt?: number
  sig: string
  handle: string
  kids: SignupKid[]
}

export const DRAFT_KEY = 'cot_signup_draft'

function freshDraft(): SignupDraft {
  return {
    step: 1,
    mode: 'family',
    name: '',
    email: '',
    dob: '',
    rel: 'parent',
    notice: false,
    noticeVersion: null,
    acks: {},
    ackVersions: {},
    method: null,
    sig: '',
    handle: '',
    kids: [{ first: '', dob: '', supervision: 'strict', consent: false }],
  }
}

export function loadSignupDraft(): SignupDraft {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SignupDraft> & { verified?: boolean }
      if (parsed.savedAt && Date.now() - parsed.savedAt > 24 * 60 * 60 * 1000) {
        sessionStorage.removeItem(DRAFT_KEY)
        return freshDraft()
      }
      // Older prototypes persisted a simulated verification result. It is never evidence.
      delete parsed.verified
      if (parsed.kids && (!Array.isArray(parsed.kids) || parsed.kids.some((k) => !k || typeof k.first !== 'string' || typeof k.dob !== 'string'))) return freshDraft()
      const draft = { ...freshDraft(), ...parsed, step: Math.min(5, Math.max(1, parsed.step ?? 1)) }
      if (draft.notice && draft.noticeVersion !== DATA_NOTICE_VERSION) {
        // Keep the family details, but require a fresh consent act against the current notice.
        return { ...draft, step: 2, notice: false, sig: '', kids: draft.kids.map((kid) => ({ ...kid, consent: false })) }
      }
      return draft
    }
  } catch {
    /* ignore corrupt draft */
  }
  return freshDraft()
}

const REL_LABEL: Record<Relationship, string> = {
  parent: 'Parent',
  guardian: 'Legal guardian',
  foster: 'Foster carer',
  kinship: 'Kinship carer',
  self: 'Adult, just me',
}

const SUP_LABEL: Record<Supervision, string> = {
  strict: 'parent approves everything',
  view: 'view only',
  self: 'sends their own requests',
}

export const VERIFICATION_METHODS: Array<{ key: VerificationMethod; label: string; body: string; available: boolean }> = [
  { key: 'card', label: 'Card payment · $1/month', body: '$1 USD every month, renewing automatically until you cancel. Hosted Stripe Checkout confirms a real card payment for parental accountability; it does not prove age or family relationship.', available: true },
  { key: 'id', label: 'Photo ID', body: 'Not available yet. An identity provider and review process are still needed.', available: false },
  { key: 'form', label: 'Signed consent form', body: 'Not available yet. Secure intake and a review team are still needed.', available: false },
  { key: 'call', label: 'Video call with a moderator', body: 'Not available yet. Trained reviewers and appointment scheduling are still needed.', available: false },
]

export function buildCompletionRequest(draft: SignupDraft): CompleteAccountRequest {
  if (!draft.notice || draft.noticeVersion !== DATA_NOTICE_VERSION) {
    throw new Error('Please review the current data notice and consent before completing setup.')
  }
  return {
    persons: draft.mode === 'solo' ? [] : draft.kids.map((k) => ({ firstName: k.first.trim(), dateOfBirth: k.dob, supervision: k.supervision, consentAccepted: k.consent })),
    relationship: draft.mode === 'solo' ? 'self' : draft.rel,
    noticeVersion: draft.noticeVersion,
    noticeAccepted: draft.notice,
    esignatureName: draft.sig.trim(),
  }
}

export function canCompleteDraft(draft: SignupDraft, familyId?: string): boolean {
  return !!familyId && draft.familyId === familyId && draft.notice && draft.noticeVersion === DATA_NOTICE_VERSION && !!draft.sig.trim()
    && draft.sig.trim().toLowerCase() === draft.name.trim().toLowerCase()
    && (draft.mode === 'solo' || (draft.kids.length > 0 && draft.kids.every((k) => k.first.trim() && ageFromDob(k.dob) !== null && k.consent)))
}

export interface SignupWizard {
  draft: SignupDraft
  solo: boolean
  step: number
  labels: string[]
  // setters
  setMode: (m: SignupMode) => void
  setName: (v: string) => void
  setEmail: (v: string) => void
  setDob: (v: string) => void
  setRel: (v: Relationship) => void
  toggleNotice: () => void
  ackRequirements: AckRequirement[]
  ackAccepted: (kind: string) => boolean
  toggleAck: (kind: string) => void
  setLegalAcks: (kinds: string[], accepted: boolean) => void
  setMethod: (m: VerificationMethod) => void
  bindFamily: (familyId: string) => void
  acknowledgementState: 'loading' | 'error' | 'ready'
  retryAcknowledgements: () => void
  setSig: (v: string) => void
  setHandle: (v: string) => void
  addKid: () => void
  removeKid: (index: number) => 'removed' | 'min'
  setKidFirst: (index: number, v: string) => void
  setKidDob: (index: number, v: string) => void
  setKidSupervision: (index: number, v: Supervision) => 'ok' | 'locked'
  toggleKidConsent: (index: number) => void
  // derived
  holderAge: number | null
  ageNote: string
  ageTone: 'neutral' | 'positive' | 'warn'
  relShown: boolean
  step3Title: string
  step3Lede: string
  noticeLede: string
  noticeCards: ReturnType<typeof noticeCards>
  consentTick: string
  kidNote: (index: number) => { text: string; tone: 'neutral' | 'positive' | 'warn' }
  kidConsentText: (index: number) => string
  sigStatus: { text: string; tone: 'neutral' | 'positive' | 'warn'; ok: boolean }
  review: Array<{ label: string; value: string }>
  doneTitle: string
  doneBody: string
  gates: Record<number, boolean>
  hints: Record<number, string>
  currentGateOk: boolean
  currentHint: string
  nextLabel: string
  showBack: boolean
  showNav: boolean
  back: () => void
  advance: () => void
  goToReview: () => void
  buildBootstrapRequest: () => BootstrapRequest
  clearDraft: () => void
}

/**
 * The signup takeover's state machine (COTC_SignupConsent R2). Mirrors the design's signupVals()
 * gates/hints/copy exactly, adds the credential + platform-acknowledgement functional necessities
 * (D1/D2), and persists a password-free draft to sessionStorage so a crash between register and
 * bootstrap can resume at review (R1).
 */
export function useSignupWizard(): SignupWizard {
  const [draft, setDraft] = useState<SignupDraft>(loadSignupDraft)

  const ackQuery = useQuery({ queryKey: ['signup', 'acknowledgements'], queryFn: getAcknowledgements, retry: false })
  const ackRequirements = useMemo<AckRequirement[]>(() => ackQuery.data?.acknowledgements ?? [], [ackQuery.data])

  // Persist a password-free draft on every change (R1 resume). Passwords are never stored.
  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, savedAt: Date.now() }))
    } catch {
      /* storage may be unavailable */
    }
  }, [draft])

  const patch = useCallback((p: Partial<SignupDraft>) => setDraft((d) => ({ ...d, ...p })), [])
  const patchKid = useCallback(
    (index: number, p: Partial<SignupKid>) =>
      setDraft((d) => ({ ...d, kids: d.kids.map((k, i) => (i === index ? { ...k, ...p } : k)) })),
    [],
  )

  const solo = draft.mode === 'solo'
  const holderAge = ageFromDob(draft.dob)
  const adult = holderAge !== null && holderAge >= 18
  const kidAges = draft.kids.map((k) => ageFromDob(k.dob))
  const legal = draft.name.trim()
  const sig = draft.sig.trim()
  const sigOk = sig.length > 0 && sig.toLowerCase() === legal.toLowerCase()
  const named = draft.kids.filter((k, i) => k.first.trim().length > 0 && kidAges[i] !== null)
  const allConsented = draft.kids.length > 0 && draft.kids.every((k) => k.consent)
  const handle = draft.handle.trim()

  const allAcksAccepted = ackQuery.isSuccess && ackRequirements.length > 0 && ackRequirements.every((a) => draft.acks[a.kind] === true && draft.ackVersions[a.kind] === a.copyVersion)

  const labels = solo
    ? ['Your account', 'What we collect', 'Your player profile', 'Confirm', 'Review', 'Done']
    : ['Your account', 'What we collect', 'Their profiles', 'Consent', 'Review', 'Done']

  const gates: Record<number, boolean> = {
    1: legal.length > 2 && /.+@.+\..+/.test(draft.email) && adult,
    2: draft.notice && draft.noticeVersion === DATA_NOTICE_VERSION && allAcksAccepted,
    3: solo ? handle.length > 1 : named.length === draft.kids.length && draft.kids.length > 0,
    4: draft.method === 'card' && sigOk && (solo || allConsented),
    5: false,
  }
  gates[5] = gates[1] && gates[2] && gates[3] && gates[4]

  const hints: Record<number, string> = {
    1:
      adult === false && draft.dob
        ? 'An account holder has to be 18 or over — a child cannot sign themselves up here.'
        : 'Your legal name and a date of birth that makes you an adult.',
    2: gates[2] ? 'Ready to record when you create your account.' : 'Tick every agreement, and confirm you have read the notice.',
    3: solo ? 'Pick the handle other players will see.' : 'Every person needs a first name and an exact date of birth.',
    4: draft.method !== 'card'
      ? 'Choose the $1/month card payment to continue.'
      : !solo && !allConsented
        ? 'Consent is per person — tick each one.'
        : !sigOk
          ? `Your signature must match “${legal || 'your legal name'}” exactly.`
          : 'Ready to sign. Your consent record is available after account setup.',
    5: 'Next, create your adult account and continue to secure payment. Child profiles wait for verified payment.',
  }

  const ageNote = holderAge === null
    ? 'An account holder must be 18 or over. Enter your date of birth; this is your age attestation.'
    : adult
      ? `The date you entered makes you ${holderAge}. This is your age attestation, not an identity check. Every managed profile remains your responsibility.`
      : `You are ${holderAge}. Only an adult of 18 or over can hold an account and consent for a child. Ask a parent or guardian to sign up instead.`
  const ageTone: 'neutral' | 'positive' | 'warn' = holderAge === null ? 'neutral' : adult ? 'positive' : 'warn'

  const kidNote = (index: number): { text: string; tone: 'neutral' | 'positive' | 'warn' } => {
    const a = kidAges[index]
    if (a === null) return { text: 'Enter a date of birth and we will tell you exactly which protections apply.', tone: 'neutral' }
    if (a < 13)
      return {
        text: `${a} years old. COPPA applies: verifiable parental consent required below, parent approval locked on, profile invisible outside your circles.`,
        tone: 'warn',
      }
    return {
      text: `${a} years old. Over the COPPA line, so consent is our own rule rather than the law’s — we still ask for it, and the account stays under you.`,
      tone: 'positive',
    }
  }

  const kidConsentText = (index: number): string => {
    const k = draft.kids[index]
    const who = k.first.trim() || `Person ${index + 1}`
    return signupChildConsentText(who, draft.rel, kidAges[index])
  }

  const sigStatus = !draft.sig
    ? { text: 'Unsigned.', tone: 'neutral' as const, ok: false }
    : sigOk
      ? { text: `Signature entered as ${legal} · ${todayEnGb()}`, tone: 'positive' as const, ok: true }
      : { text: 'This does not match the name on the account.', tone: 'warn' as const, ok: false }

  const review: Array<{ label: string; value: string }> = [
    { label: 'Account type', value: solo ? 'Adult, just me — no child profiles' : `Family · ${REL_LABEL[draft.rel]}` },
    { label: 'Account holder', value: (draft.name || '—') + (holderAge === null ? '' : ` · ${holderAge}`) },
    { label: 'Email', value: draft.email || '—' },
    { label: 'Parent verification', value: 'Pending — $1 USD/month card payment after account creation' },
    solo
      ? { label: 'Your handle', value: handle || '—' }
      : {
          label: 'Your family',
          value: draft.kids
            .map((k, i) => `${k.first.trim() || 'Unnamed'} (${kidAges[i] === null ? 'no DOB' : kidAges[i]}, ${SUP_LABEL[k.supervision]})`)
            .join(' · '),
        },
    {
      label: solo ? 'Agreed' : 'Consent',
      value: solo
        ? sigOk
          ? `Terms and notice selected, signature entered by ${legal}`
          : 'Incomplete'
        : allConsented && sigOk
          ? `Consent selected for all ${draft.kids.length}; recorded after payment and profile completion`
          : 'Incomplete',
    },
    {
      label: 'Collected',
      value: solo
        ? 'Your account details, age attestation, card payment evidence, handle, circle memberships and safety reports, as described in the preview notice.'
        : 'Account details and attestations, card payment evidence and, after verified payment, managed profile details described in the preview notice.',
    },
    { label: 'Profile limits', value: 'Player profiles do not ask for school, address, phone or photos. Payment information is entered separately in Stripe checkout.' },
  ]

  const doneTitle = 'Your adult account is saved.'
  const doneBody = 'Continue to Parent verification to confirm payment and finish setup.'

  const setKidSupervision = (index: number, v: Supervision): 'ok' | 'locked' => {
    const a = kidAges[index]
    if (v === 'self' && a !== null && a < 13) return 'locked'
    patchKid(index, { supervision: v })
    return 'ok'
  }

  const setKidDob = (index: number, v: string) => {
    const na = ageFromDob(v)
    // Switching a kid under 13 forces supervision → strict and clears their consent tick.
    patchKid(index, { dob: v, supervision: na !== null && na < 13 ? 'strict' : draft.kids[index].supervision, consent: false })
  }

  const removeKid = (index: number): 'removed' | 'min' => {
    if (draft.kids.length <= 1) return 'min'
    setDraft((d) => ({ ...d, kids: d.kids.filter((_, i) => i !== index) }))
    return 'removed'
  }

  const buildBootstrapRequest = (): BootstrapRequest => ({
    mode: draft.mode,
    legalName: legal,
    email: draft.email.trim(),
    dateOfBirth: draft.dob,
    relationship: solo ? 'self' : draft.rel,
    handle: solo ? handle : undefined,
    noticeVersion: draft.noticeVersion ?? '',
    noticeAccepted: draft.notice,
    verificationMethod: (draft.method ?? 'card') as VerificationMethod,
    esignatureName: sig,
    deferManagedProfiles: true,
    persons: [],
  })

  const clearDraft = () => {
    try {
      sessionStorage.removeItem(DRAFT_KEY)
    } catch {
      /* ignore */
    }
  }

  return {
    draft,
    solo,
    step: draft.step,
    labels,
    setMode: (m) => {
      if (m !== draft.mode) patch({ mode: m, rel: m === 'solo' ? 'self' : 'parent', notice: false, noticeVersion: null, sig: '', kids: draft.kids.map((kid) => ({ ...kid, consent: false })) })
    },
    setName: (v) => patch({ name: v }),
    setEmail: (v) => patch({ email: v }),
    setDob: (v) => patch({ dob: v }),
    setRel: (v) => setDraft((d) => ({ ...d, rel: v, kids: d.kids.map((k) => ({ ...k, consent: false })) })),
    toggleNotice: () => patch({ notice: !draft.notice, noticeVersion: !draft.notice ? DATA_NOTICE_VERSION : null, sig: '', kids: draft.kids.map((kid) => ({ ...kid, consent: false })) }),
    ackRequirements,
    ackAccepted: (kind) => draft.acks[kind] === true && draft.ackVersions[kind] === ackRequirements.find((a) => a.kind === kind)?.copyVersion,
    toggleAck: (kind) => patch({ acks: { ...draft.acks, [kind]: !draft.acks[kind] || draft.ackVersions[kind] !== ackRequirements.find((a) => a.kind === kind)?.copyVersion }, ackVersions: { ...draft.ackVersions, [kind]: ackRequirements.find((a) => a.kind === kind)?.copyVersion ?? '' } }),
    /** Set multiple acknowledgement kinds in one draft patch (avoids stale sequential toggles). */
    setLegalAcks: (kinds: string[], accepted: boolean) => {
      const nextAcks = { ...draft.acks }
      const nextVersions = { ...draft.ackVersions }
      for (const kind of kinds) {
        const req = ackRequirements.find((a) => a.kind === kind)
        nextAcks[kind] = accepted
        nextVersions[kind] = accepted ? (req?.copyVersion ?? '') : ''
      }
      patch({ acks: nextAcks, ackVersions: nextVersions })
    },
    setMethod: (m) => { if (m === 'card') patch({ method: m }) },
    bindFamily: (familyId) => {
      if (draft.familyId && draft.familyId !== familyId) {
        const cleared = { ...freshDraft(), familyId, savedAt: Date.now() }
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(cleared))
        setDraft(cleared)
        return
      }
      const bound = { ...draft, familyId, savedAt: Date.now() }
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(bound))
      patch({ familyId })
    },
    acknowledgementState: ackQuery.isPending ? 'loading' : ackQuery.isError || ackRequirements.length === 0 ? 'error' : 'ready',
    retryAcknowledgements: () => { void ackQuery.refetch() },
    setSig: (v) => patch({ sig: v }),
    setHandle: (v) => patch({ handle: v }),
    addKid: () => setDraft((d) => ({ ...d, kids: [...d.kids, { first: '', dob: '', supervision: 'strict', consent: false }] })),
    removeKid,
    setKidFirst: (index, v) => patchKid(index, { first: v, consent: false }),
    setKidDob,
    setKidSupervision,
    toggleKidConsent: (index) => patchKid(index, { consent: !draft.kids[index].consent }),
    holderAge,
    ageNote,
    ageTone,
    relShown: !solo,
    step3Title: solo ? 'Your player profile' : 'Who are you setting up?',
    step3Lede: solo
      ? 'Adults play here too. Pick the handle other members will see — your legal name is never shown to anyone but a moderator.'
      : 'Add each person you will manage. A first name and an exact date of birth for each — we are not permitted to guess an age, and the protections we apply depend on it.',
    noticeLede: noticeLede(solo),
    noticeCards: noticeCards(solo),
    consentTick: noticeConsentTick(solo),
    kidNote,
    kidConsentText,
    sigStatus,
    review,
    doneTitle,
    doneBody,
    gates,
    hints,
    currentGateOk: gates[draft.step] ?? true,
    currentHint: hints[draft.step] ?? '',
    nextLabel: draft.step === 5 ? 'Create account' : 'Continue',
    showBack: draft.step > 1 && draft.step < 6,
    showNav: draft.step < 6,
    back: () => patch({ step: Math.max(1, draft.step - 1) }),
    advance: () => patch({ step: Math.min(6, draft.step + 1) }),
    goToReview: () => patch({ step: 5 }),
    buildBootstrapRequest,
    clearDraft,
  }
}
